import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Coins, Banknote, CalendarDays, Calculator, Info, Wallet, BriefcaseBusiness, Building2, CalendarClock, Target, FilePenLine,
  CloudUpload, IdCard, CreditCard, FileText, CircleCheck, CirclePlus, ShieldCheck, Send, ChevronDown, Loader2, PartyPopper, TriangleAlert, Save, Trash2, Camera, House, Factory, Landmark, HandCoins } from "lucide-react";
import { api, errMsg, useApi } from "@/lib/api";
import { rp, simulate } from "@/lib/format";
import { SignaturePad } from "@/components/SignaturePad";
import { stampGeoPhoto } from "@/lib/geoPhoto";

const TENORS = [6, 12, 18, 24, 36, 48, 60];
const SOURCES = ["Karyawan Swasta", "PNS / ASN", "TNI / POLRI", "Wiraswasta / Usaha", "Petani / Pekebun", "Nelayan", "Guru / Tenaga Pendidik", "Lainnya"];
const LENGTHS = ["< 1 Tahun", "1 – 2 Tahun", "> 2 Tahun", "> 5 Tahun"];
const PURPOSES = ["Modal Usaha", "Pendidikan", "Renovasi Rumah", "Kesehatan", "Kendaraan", "Pertanian / Perkebunan", "Konsumtif / Kebutuhan Keluarga", "Lainnya"];
const DOCS = [
  { type: "ktp", title: "KTP", sub: "(Wajib)", icon: IdCard, required: true },
  { type: "slip", title: "Slip Gaji /\nBukti Penghasilan", sub: "(Wajib)", icon: CreditCard, required: true },
  { type: "selfie", title: "Selfie\ndengan KTP", sub: "(Wajib • Kamera)", icon: Camera, required: true, capture: "user" },
  { type: "rumah", title: "Foto Rumah /\nTempat Usaha", sub: "(Wajib • Kamera + Lokasi)", icon: House, required: true, capture: "environment" },
  { type: "pendukung", title: "Dokumen Pendukung", sub: "(Opsional)", icon: FileText },
];
const EMPTY = { income_monthly: 0, income_source: "", workplace: "", job_desc: "", work_length: "", purpose_category: "", purpose_note: "",
  disburse_method: "", bank_name: "", bank_account: "", bank_holder: "" };
const METHODS = [
  { k: "sukarela", t: "Simpanan Sukarela", d: "Langsung masuk ke saldo sukarela Anda", icon: Wallet },
  { k: "tunai", t: "Tunai di Kantor CU", d: "Ambil dana di kantor pelayanan", icon: Building2 },
  { k: "bank", t: "Transfer Bank", d: "Ke rekening bank atas nama Anda", icon: Landmark },
];
const num = (v) => Math.min(Number(String(v).replace(/\D/g, "")) || 0, 1000000000);

const Hero = ({ onBack, title }) => (
  <div className="relative overflow-hidden bg-gradient-to-r from-[#0b3a9e] via-[#1450cf] to-[#1d5fe0] text-white px-4 pt-4 pb-12 -mt-3">
    <svg className="absolute right-0 bottom-2 w-36 h-20 pointer-events-none" viewBox="0 0 200 100" aria-hidden="true">
      <path d="M150 30 C175 18 192 12 200 10 V20 C185 24 168 32 152 40 Z" fill="#ef4444" />
      <path d="M0 100 L60 62 L82 74 L128 22 L170 70 L200 58 V100 Z" fill="white" opacity="0.95" />
      <path d="M60 62 L82 74 L96 60 L128 22 L118 54 L100 70 Z" fill="#bfdbfe" opacity="0.6" />
      <path d="M0 100 L60 62 L80 76 L200 92 V100 Z" fill="#1d4ed8" opacity="0.5" />
    </svg>
    <div className="relative z-10 flex items-start gap-3">
      <button onClick={onBack} data-testid="loan-back" aria-label="Kembali" className="mt-1.5"><ArrowLeft className="h-6 w-6" /></button>
      <div>
        <h1 className="font-display font-extrabold text-[24px] leading-tight" data-testid="loan-title">{title}</h1>
        <p className="text-[12.5px] text-blue-50 [text-shadow:0_1px_2px_rgba(0,0,0,0.25)]">Pengajuan pinjaman anggota CU Tuah Talino</p>
      </div>
    </div>
  </div>
);

const STEPS = ["Formulir\nPengajuan", "Upload\nDokumen", "Konfirmasi\nData", "Selesai"];
const Stepper = ({ step }) => (
  <div className="relative grid grid-cols-4 px-2 pt-4 pb-2" data-testid="loan-stepper">
    <div className="absolute left-[12.5%] right-[12.5%] top-[30px] h-[3px] bg-slate-200" />
    <div className="absolute left-[12.5%] top-[30px] h-[3px] bg-blue-700 transition-[width] duration-500" style={{ width: `${(Math.min(step, 4) - 1) * 25 + (step < 4 ? 6 : 0)}%` }} />
    {STEPS.map((l, i) => {
      const on = i + 1 <= step;
      return (
        <div key={l} className="relative flex flex-col items-center text-center" data-testid={`loan-step-${i + 1}`}>
          <span className={`h-[30px] w-[30px] rounded-full flex items-center justify-center text-sm font-extrabold ring-4 ring-white ${on ? "bg-blue-700 text-white shadow-md shadow-blue-700/40" : "bg-slate-300 text-white"}`}>{i + 1}</span>
          <span className={`text-[11px] leading-tight mt-1 whitespace-pre-line ${i + 1 === step ? "font-bold text-blue-800" : "text-slate-500"}`}>{l}</span>
        </div>
      );
    })}
  </div>
);

const Section = ({ icon: Icon, title, sub, children, testid }) => (
  <section className="bg-white rounded-2xl border border-slate-100 shadow-[0_2px_10px_rgba(15,23,42,0.06)] p-3.5" data-testid={testid}>
    <div className="flex items-start gap-3 mb-3">
      <span className="h-11 w-11 rounded-xl bg-gradient-to-br from-blue-600 to-blue-800 text-white flex items-center justify-center shrink-0 shadow-md shadow-blue-700/30"><Icon className="h-5 w-5" /></span>
      <div><h2 className="font-display font-extrabold text-[16px] text-[#0b1437] leading-tight">{title}</h2><p className="text-[11.5px] text-slate-600">{sub}</p></div>
    </div>
    {children}
  </section>
);

const Label = ({ icon: Icon, children }) => <div className="flex items-center gap-1.5 text-[12px] text-slate-800 mb-1"><Icon className="h-4 w-4 text-slate-700" strokeWidth={1.7} />{children}</div>;
const box = "w-full h-11 rounded-xl border border-slate-300 bg-white px-3 text-[15px] text-slate-900 outline-none focus:ring-2 focus:ring-blue-600/40";

const MoneyInput = ({ value, onChange, testid }) => (
  <div className={`${box} flex items-center gap-2`}>
    <span className="text-slate-500">Rp</span>
    <input inputMode="numeric" value={value ? value.toLocaleString("id-ID") : ""} onChange={(e) => onChange(num(e.target.value))} placeholder="0"
      data-testid={testid} className="w-full bg-transparent outline-none font-semibold text-[16px]" />
  </div>
);

const Pick = ({ value, onChange, options, testid, placeholder, fmt = (o) => o }) => (
  <div className="relative">
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} className={`${box} appearance-none pr-9`}>
      {placeholder && <option value="">{placeholder}</option>}
      {options.map((o) => <option key={o} value={o}>{fmt(o)}</option>)}
    </select>
    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-700 pointer-events-none" />
  </div>
);

const SimBox = ({ s, rate }) => (
  <div className="rounded-2xl overflow-hidden bg-gradient-to-r from-blue-50 to-blue-100/80 border border-blue-100 mt-3" data-testid="loan-simulation-result">
    <div className="flex items-center gap-2 px-3 py-3">
      <span className="h-10 w-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-700 text-white flex items-center justify-center shrink-0"><Calculator className="h-5 w-5" /></span>
      <div className="leading-tight pr-2 border-r border-blue-200"><div className="font-bold text-[13px] text-[#0b1437]">Simulasi Angsuran</div><div className="text-[10.5px] text-slate-600">(Perkiraan)</div></div>
      <div className="flex-1 text-center px-1 border-r border-blue-200"><div className="text-[10px] text-slate-600">Angsuran per Bulan</div><div className="font-display font-extrabold text-[17px] text-blue-800" data-testid="sim-installment">{rp(s.installment)}</div></div>
      <div className="text-center pl-1"><div className="text-[10px] text-slate-600">Bunga ({String(rate).replace(".", ",")}%/bulan)</div><div className="font-display font-extrabold text-[15px] text-[#0b1437]" data-testid="sim-interest">{rp(s.interest)}</div></div>
    </div>
    <div className="bg-blue-100/70 border-t border-blue-200/70 px-2 py-2 flex flex-wrap justify-center gap-x-2 text-[10px] text-slate-700">
      <span>Pokok/bulan: <b>{rp(s.principal)}</b></span><span className="text-blue-300">|</span>
      <span>Total Bunga: <b>{rp(s.totalInterest)}</b></span><span className="text-blue-300">|</span>
      <span>Total Pembayaran: <b data-testid="sim-total">{rp(s.installment * s.tenor)}</b></span>
    </div>
  </div>
);

const DSR_LIMIT = 30;
const DsrWarning = ({ installment, income }) => {
  if (!income || !installment) return null;
  const pct = (installment / income) * 100;
  if (pct <= DSR_LIMIT) return (
    <p className="flex items-center gap-1.5 text-[11.5px] text-emerald-700 mt-2.5" data-testid="loan-dsr-ok"><CircleCheck className="h-4 w-4 shrink-0" />Angsuran {pct.toFixed(1).replace(".", ",")}% dari penghasilan — dalam batas wajar.</p>
  );
  return (
    <div className="mt-3 flex gap-2.5 rounded-xl border border-amber-300 bg-amber-50 p-3" data-testid="loan-dsr-warning" role="alert">
      <TriangleAlert className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
      <div className="text-[12px] text-amber-900 leading-snug">
        <b>Angsuran {pct.toFixed(1).replace(".", ",")}% dari penghasilan</b> (batas aman {DSR_LIMIT}%). Pertimbangkan menurunkan jumlah pinjaman atau memperpanjang tenor. Maks. angsuran disarankan <b>{rp(Math.floor(income * DSR_LIMIT / 100))}</b>/bulan. Pengajuan tetap dapat dikirim.
      </div>
    </div>
  );
};

const DocTile = ({ d, doc, busy, onPick }) => {
  const done = !!doc;
  return (
    <button type="button" onClick={onPick} disabled={busy} data-testid={`loan-doc-${d.type}`}
      className={`relative rounded-2xl border-2 px-1.5 pt-4 pb-3 flex flex-col items-center text-center transition-colors ${done ? "border-emerald-500 bg-white" : "border-blue-100 bg-blue-50/50 hover:border-blue-300"}`}>
      <span className="absolute top-1.5 right-1.5">{busy ? <Loader2 className="h-5 w-5 animate-spin text-blue-600" /> : done ? <CircleCheck className="h-5 w-5 fill-emerald-500 text-white" /> : <CirclePlus className="h-5 w-5 fill-blue-300 text-white" />}</span>
      <d.icon className="h-8 w-8 text-blue-700" strokeWidth={1.6} />
      <span className="text-[11px] font-bold text-slate-900 leading-tight mt-1.5 whitespace-pre-line">{d.title}</span>
      <span className="text-[11px] text-slate-600">{d.sub}</span>
      {doc && <span className="text-[9.5px] text-emerald-700 truncate max-w-full mt-0.5" data-testid={`loan-doc-${d.type}-name`}>{doc.name}</span>}
    </button>
  );
};

export default function LoanApply() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const simOnly = params.get("mode") === "simulasi";
  const { data: settings } = useApi("/settings");
  const [amount, setAmount] = useState(num(params.get("amount") || 5000000));
  const [tenor, setTenor] = useState(Number(params.get("tenor")) || 12);
  const [f, setF] = useState(EMPTY);
  const [docs, setDocs] = useState({});
  const [uploading, setUploading] = useState(null);
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [draftReady, setDraftReady] = useState(simOnly);
  const [savedAt, setSavedAt] = useState(null);
  const fileRef = useRef(null);
  const camRef = useRef(null);
  const pending = useRef(null);
  const rate = settings?.loan_rate ?? 1.5;
  const s = { ...simulate(amount, tenor, rate), tenor };
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));

  const dataOk = amount > 0 && f.income_monthly > 0 && f.income_source && f.workplace.trim() && f.job_desc.trim() && f.work_length && f.purpose_category && f.disburse_method &&
    (f.disburse_method !== "bank" || (f.bank_name.trim() && f.bank_account.replace(/\D/g, "").length >= 6 && f.bank_holder.trim()));
  const docsOk = docs.ktp && docs.slip && docs.selfie && docs.rumah;
  const touched = useRef(false);

  useEffect(() => {
    if (simOnly) return;
    api.get("/loans/draft").then(({ data }) => {
      const d = data?.data;
      if (!d) return;
      if (!params.get("amount") && d.amount) setAmount(num(d.amount));
      if (!params.get("tenor") && d.tenor) setTenor(Number(d.tenor));
      if (d.f) setF((p) => ({ ...p, ...d.f }));
      if (d.docs) setDocs(d.docs);
      if (d.agreed) setAgreed(true);
      setSavedAt(data.updated_at);
      touched.current = true;
      toast.success("Draf pengajuan dipulihkan");
    }).catch(() => {}).finally(() => setDraftReady(true));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!draftReady || simOnly || done) return;
    if (!touched.current) { touched.current = true; return; }
    const t = setTimeout(() => {
      api.put("/loans/draft", { data: { amount, tenor, f, docs, agreed } }).then(({ data }) => setSavedAt(data.updated_at)).catch(() => {});
    }, 1200);
    return () => clearTimeout(t);
  }, [amount, tenor, f, docs, agreed, draftReady, simOnly, done]);

  const clearDraft = async () => {
    setDraftReady(false);
    await api.delete("/loans/draft").catch(() => {});
    touched.current = false;
    setAmount(5000000); setTenor(12); setDocs({}); setAgreed(false); setSavedAt(null);
    setF(EMPTY);
    toast.success("Draf dihapus");
    setTimeout(() => setDraftReady(true), 0);
  };
  const step = done ? 4 : !dataOk ? 1 : !docsOk ? 2 : 3;

  const pick = (d) => {
    pending.current = d.type;
    const el = d.capture ? camRef.current : fileRef.current;
    if (d.capture) el?.setAttribute("capture", d.capture);
    el?.click();
  };
  const upload = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (file && pending.current) sendDoc(pending.current, file, file.name);
  };
  const sendDoc = async (type, file, name) => {
    if (type !== "rumah" && file.size > 5 * 1024 * 1024) return toast.error("Ukuran dokumen maksimal 5 MB");
    setUploading(type);
    try {
      const fd = new FormData();
      fd.append("doc_type", type);
      if (type === "rumah") {
        const { blob, geo } = await stampGeoPhoto(file);
        fd.append("file", blob, "foto-rumah.jpg");
        Object.entries(geo).forEach(([k, v]) => fd.append(k, String(v)));
      } else fd.append("file", file, name);
      const { data } = await api.post("/loans/docs", fd);
      setDocs((p) => ({ ...p, [type]: data }));
    } catch (err) {
      toast.error(err?.response ? errMsg(err) : err.message || errMsg(err));
    } finally {
      setUploading(null);
    }
  };

  const submit = async () => {
    if (!dataOk) return toast.error("Lengkapi data pinjaman, penghasilan, tujuan, dan metode pencairan");
    if (!docsOk) return toast.error("Lengkapi KTP, Slip Gaji, Selfie dengan KTP, dan Foto Rumah/Tempat Usaha");
    if (!agreed) return toast.error("Centang pernyataan anggota");
    if (!docs.ttd) return toast.error("Bubuhkan tanda tangan digital Anda");
    setBusy(true);
    try {
      const purpose = f.purpose_note.trim() ? `${f.purpose_category} – ${f.purpose_note.trim()}` : f.purpose_category;
      await api.post("/loans", { amount, tenor_months: tenor, purpose, ...f, doc_ids: Object.values(docs).map((d) => d.doc_id), agreed });
      setDone(true);
      setSavedAt(null);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="loan-apply-page">
      <Hero title={simOnly ? "Simulasi Kredit" : "Ajukan Pinjaman"} onBack={() => navigate("/app/pinjaman")} />
      <div className="relative -mt-7 bg-[#f8fafc] rounded-t-[28px] px-3.5 pb-6 space-y-3.5">
        {!simOnly && <Stepper step={step} />}
        {simOnly && <div className="pt-4" />}
        {done ? (
          <div className="bg-white rounded-2xl border border-emerald-100 p-6 text-center space-y-3 shadow-sm" data-testid="loan-success">
            <PartyPopper className="h-12 w-12 text-emerald-600 mx-auto" />
            <h2 className="font-display font-extrabold text-xl text-[#0b1437]">Pengajuan Terkirim</h2>
            <p className="text-sm text-slate-600">Pengajuan pinjaman {rp(amount)} selama {tenor} bulan sedang ditinjau pengurus CU. Anda akan menerima notifikasi setelah ada keputusan.</p>
            <button onClick={() => navigate("/app/pinjaman")} data-testid="loan-success-done" className="w-full bg-blue-700 hover:bg-blue-800 text-white font-bold py-3 rounded-xl">Lihat Pinjaman Saya</button>
          </div>
        ) : (
          <>
            <Section icon={Coins} title="Data Pinjaman" sub="Isi jumlah, tenor, dan tujuan pinjaman." testid="loan-section-data">
              <div className="grid grid-cols-[1.15fr_1fr] gap-3">
                <div><Label icon={Banknote}>Jumlah Pinjaman</Label><MoneyInput value={amount} onChange={setAmount} testid="loan-amount-input" /></div>
                <div><Label icon={CalendarDays}>Tenor (Jangka Waktu)</Label><Pick value={tenor} onChange={(v) => setTenor(Number(v))} options={TENORS} fmt={(t) => `${t} Bulan`} testid="loan-tenor-select" /></div>
              </div>
              <SimBox s={s} rate={rate} />
              <p className="flex items-center gap-1.5 text-[11px] text-slate-600 mt-2.5"><Info className="h-3.5 w-3.5 shrink-0" />Perhitungan bunga flat. Nilai akhir mengikuti keputusan pengurus.</p>
            </Section>

            {simOnly ? (
              <button onClick={() => navigate(`/app/pinjaman/ajukan?amount=${amount}&tenor=${tenor}`)} data-testid="sim-to-apply-button"
                className="w-full bg-gradient-to-b from-red-500 to-red-700 text-white font-bold text-lg py-3.5 rounded-2xl shadow-lg shadow-red-700/30">Ajukan dengan Simulasi Ini</button>
            ) : (
              <>
                <Section icon={Wallet} title="Data Penghasilan" sub="Informasi penghasilan dan sumber pendapatan." testid="loan-section-income">
                  <div className="grid grid-cols-2 gap-x-3 gap-y-3">
                    <div><Label icon={Banknote}>Penghasilan per Bulan</Label><MoneyInput value={f.income_monthly} onChange={set("income_monthly")} testid="loan-income-input" /></div>
                    <div><Label icon={BriefcaseBusiness}>Sumber Penghasilan</Label><Pick value={f.income_source} onChange={set("income_source")} options={SOURCES} placeholder="Pilih" testid="loan-income-source" /></div>
                    <div className="col-span-2"><Label icon={Factory}>Tempat Bekerja / Nama Usaha</Label><input value={f.workplace} onChange={(e) => set("workplace")(e.target.value)} maxLength={120} placeholder="Contoh: PT Sinar Kapuas, Pontianak" data-testid="loan-workplace-input" className={box} /></div>
                    <div><Label icon={Building2}>Keterangan Pekerjaan</Label><input value={f.job_desc} onChange={(e) => set("job_desc")(e.target.value)} maxLength={120} placeholder="Contoh: Staff IT" data-testid="loan-job-input" className={box} /></div>
                    <div><Label icon={CalendarClock}>Lama Bekerja/Usaha</Label><Pick value={f.work_length} onChange={set("work_length")} options={LENGTHS} placeholder="Pilih" testid="loan-work-length" /></div>
                  </div>
                  <DsrWarning installment={s.installment} income={f.income_monthly} />
                </Section>

                <Section icon={Target} title="Tujuan Pinjaman" sub="Pilih tujuan penggunaan dana." testid="loan-section-purpose">
                  <Pick value={f.purpose_category} onChange={set("purpose_category")} options={PURPOSES} placeholder="Pilih tujuan pinjaman" testid="loan-purpose-select" />
                  <div className="mt-3"><Label icon={FilePenLine}>Keterangan Tujuan (Opsional)</Label>
                    <textarea value={f.purpose_note} onChange={(e) => set("purpose_note")(e.target.value)} rows={2} maxLength={500} data-testid="loan-purpose-input"
                      placeholder="Contoh: penambahan modal usaha" className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-[15px] outline-none focus:ring-2 focus:ring-blue-600/40" />
                  </div>
                </Section>

                <Section icon={HandCoins} title="Metode Pencairan" sub="Pilih cara menerima dana jika pinjaman disetujui." testid="loan-section-method">
                  <div className="space-y-2">
                    {METHODS.map((m) => (
                      <button type="button" key={m.k} onClick={() => set("disburse_method")(m.k)} data-testid={`loan-method-${m.k}`}
                        className={`w-full flex items-center gap-3 rounded-xl border-2 px-3 py-2.5 text-left transition-colors ${f.disburse_method === m.k ? "border-blue-600 bg-blue-50" : "border-slate-200 bg-white hover:border-blue-300"}`}>
                        <m.icon className="h-5 w-5 text-blue-700 shrink-0" />
                        <span className="flex-1"><span className="block text-[13.5px] font-bold text-slate-900">{m.t}</span><span className="block text-[11.5px] text-slate-600">{m.d}</span></span>
                        <span className={`h-4 w-4 rounded-full border-2 ${f.disburse_method === m.k ? "border-blue-700 bg-blue-700 ring-2 ring-white ring-inset" : "border-slate-300"}`} />
                      </button>
                    ))}
                  </div>
                  {f.disburse_method === "bank" && (
                    <div className="grid grid-cols-2 gap-3 mt-3" data-testid="loan-bank-fields">
                      <div><Label icon={Landmark}>Nama Bank</Label><input value={f.bank_name} onChange={(e) => set("bank_name")(e.target.value)} maxLength={60} placeholder="Contoh: BRI" data-testid="loan-bank-name" className={box} /></div>
                      <div><Label icon={CreditCard}>No. Rekening</Label><input inputMode="numeric" value={f.bank_account} onChange={(e) => set("bank_account")(e.target.value.replace(/\D/g, ""))} maxLength={30} data-testid="loan-bank-account" className={box} /></div>
                      <div className="col-span-2"><Label icon={IdCard}>Nama Pemilik Rekening</Label><input value={f.bank_holder} onChange={(e) => set("bank_holder")(e.target.value)} maxLength={80} data-testid="loan-bank-holder" className={box} /></div>
                    </div>
                  )}
                </Section>

                <Section icon={CloudUpload} title="Unggah Dokumen" sub="Dokumen foto/PDF maks. 5 MB. Selfie & foto rumah wajib diambil langsung dengan kamera." testid="loan-section-docs">
                  <div className="grid grid-cols-3 gap-2">
                    {DOCS.map((d) => <DocTile key={d.type} d={d} doc={docs[d.type]} busy={uploading === d.type} onPick={() => pick(d)} />)}
                  </div>
                  <input ref={fileRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={upload} data-testid="loan-doc-input" />
                  <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={upload} data-testid="loan-camera-input" />
                </Section>

                <Section icon={ShieldCheck} title="Pernyataan Anggota" sub="" testid="loan-section-agree">
                  <label className="flex items-start gap-3 cursor-pointer -mt-1">
                    <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} data-testid="loan-agree" className="mt-0.5 h-5 w-5 accent-blue-700 shrink-0" />
                    <span className="text-[12px] text-slate-700 leading-snug">Saya menyatakan bahwa data yang saya berikan adalah benar, lengkap, dan dapat dipertanggungjawabkan. Saya bersedia mengikuti ketentuan dan keputusan pengurus CU Tuah Talino.</span>
                  </label>
                  <div className="mt-3"><Label icon={FilePenLine}>Tanda Tangan Digital (Wajib)</Label>
                    <SignaturePad doc={docs.ttd} busy={uploading === "ttd"} onSave={(b) => sendDoc("ttd", b, "tanda-tangan.png")}
                      onReset={() => setDocs((p) => { const { ttd, ...rest } = p; return rest; })} />
                  </div>
                </Section>

                {savedAt && (
                  <div className="flex items-center justify-between text-[11.5px] text-slate-600 px-1" data-testid="loan-draft-status">
                    <span className="flex items-center gap-1.5"><Save className="h-3.5 w-3.5 text-blue-700" />Draf tersimpan otomatis {new Date(savedAt).toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" })}</span>
                    <button type="button" onClick={clearDraft} data-testid="loan-draft-clear" className="flex items-center gap-1 font-semibold text-red-600"><Trash2 className="h-3.5 w-3.5" />Hapus draf</button>
                  </div>
                )}

                <button onClick={submit} disabled={busy} data-testid="submit-loan-button"
                  className="w-full flex items-center justify-center gap-3 bg-gradient-to-b from-red-500 to-red-700 hover:from-red-600 disabled:opacity-60 text-white font-bold text-lg py-3.5 rounded-2xl shadow-lg shadow-red-700/30 active:scale-[0.98] transition-transform">
                  {busy ? <Loader2 className="h-6 w-6 animate-spin" /> : <Send className="h-6 w-6" />} Kirim Pengajuan
                </button>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
